import { useQuery } from "@tanstack/react-query";
import { useUser, useOrganization } from "@clerk/clerk-react";
import type { CustomPart } from "@onsemble/shared";
import { getCustomParts } from "@/requests/custom-parts/get-custom-parts";
import { useCreateCustomPart } from "@/requests/custom-parts/create-custom-part";
import { useUpdateCustomPart } from "@/requests/custom-parts/update-custom-part";
import { useDeleteCustomPart } from "@/requests/custom-parts/delete-custom-part";

interface UseCustomPartsResult {
  customParts: CustomPart[];
  isLoading: boolean;
  error: unknown;
  createCustomPart: (data: { title: string; cost: number }) => Promise<any>;
  updateCustomPart: (
    customPartId: string,
    data: { cost?: number; title?: string },
  ) => Promise<any>;
  deleteCustomPart: (customPartId: string) => Promise<any>;
}

export const useCustomParts = (): UseCustomPartsResult => {
  const { user } = useUser();
  const { organization } = useOrganization();

  const { data, isLoading, error } = useQuery({
    ...getCustomParts(),
    enabled: !!user && !!organization,
  });
  const createCustomPartMutation = useCreateCustomPart();
  const updateCustomPartMutation = useUpdateCustomPart();
  const deleteCustomPartMutation = useDeleteCustomPart();

  const createCustomPart = async (data: { title: string; cost: number }) => {
    return createCustomPartMutation.mutateAsync(data);
  };

  const updateCustomPart = async (
    customPartId: string,
    data: { cost?: number; title?: string },
  ) => {
    return updateCustomPartMutation.mutateAsync({ customPartId, data });
  };

  const deleteCustomPart = async (customPartId: string) => {
    return deleteCustomPartMutation.mutateAsync(customPartId);
  };

  return {
    customParts: data || [],
    isLoading,
    error,
    createCustomPart,
    updateCustomPart,
    deleteCustomPart,
  };
};
